import React, { useEffect, useState, useCallback } from 'react';
import debounce from 'lodash/debounce';
import {
  Box, Typography, Chip, Button, Checkbox,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, useTheme, IconButton, Tooltip,
  Popover, TextField, Switch, FormControlLabel, Stack,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import CloseIcon from '@mui/icons-material/Close';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { InputAdornment } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  fetchMonitoringLifecycle,
  upsertRenewalAlert,
  getRenewalAlert,
  deleteRenewalAlert,
} from '../../../redux/actions/contractAction';

const lifecycleStatusConfig = {
  active: { color: 'success', key: 'monitoring.status.active' },
  expiring_soon: { color: 'warning', key: 'monitoring.status.expiringSoon' },
  expired: { color: 'error', key: 'monitoring.status.expired' },
  auto_renewing: { color: 'info', key: 'monitoring.status.autoRenewing' },
  draft: { color: 'default', key: 'monitoring.status.draft' },
  in_review: { color: 'info', key: 'monitoring.status.inReview' },
};

const LifecycleList = ({ searchTerm = '' }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const lifecycleData = useSelector(state => state.contract?.monitoringLifecycle || []);
  const loading = useSelector(state => state.contract?.loadingMonitoring || false);

  // Renewal alert popover state
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [popoverContractId, setPopoverContractId] = useState(null);
  const [renewalConfig, setRenewalConfig] = useState({ alert_days_before: 90, alert_emails: [], send_to_self: true, is_enabled: true });
  const [loadingRenewal, setLoadingRenewal] = useState(false);
  const [emailInput, setEmailInput] = useState('');

  // Debounced fetch from backend with search param
  const debouncedFetch = useCallback(
    debounce((term) => {
      const params = {};
      if (term && term.trim()) params.search = term.trim();
      dispatch(fetchMonitoringLifecycle(params));
    }, 400),
    [dispatch]
  );

  useEffect(() => {
    debouncedFetch(searchTerm);
  }, [searchTerm, debouncedFetch]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try { return new Date(dateStr).toLocaleDateString(); }
    catch { return dateStr; }
  };

  // All filtering is done by the backend
  const filtered = lifecycleData;

  // Open renewal alert popover
  const handleOpenRenewal = async (event, contractId) => {
    setPopoverAnchor(event.currentTarget);
    setPopoverContractId(contractId);
    setLoadingRenewal(true);
    try {
      const existing = await dispatch(getRenewalAlert(contractId));
      if (existing && existing.id) {
        setRenewalConfig({
          alert_days_before: existing.alert_days_before || 90,
          alert_emails: existing.alert_emails || [],
          send_to_self: existing.send_to_self !== false,
          is_enabled: existing.is_enabled !== false,
        });
      } else {
        setRenewalConfig({ alert_days_before: 90, alert_emails: [], send_to_self: true, is_enabled: true });
      }
    } catch {
      setRenewalConfig({ alert_days_before: 90, alert_emails: [], send_to_self: true, is_enabled: true });
    } finally {
      setLoadingRenewal(false);
    }
  };

  const handleCloseRenewal = () => {
    setPopoverAnchor(null);
    setPopoverContractId(null);
    setEmailInput('');
  };

  const handleSaveRenewal = async () => {
    try {
      await dispatch(upsertRenewalAlert({
        contract_id: popoverContractId,
        alert_days_before: renewalConfig.alert_days_before,
        alert_emails: renewalConfig.alert_emails,
        send_to_self: renewalConfig.send_to_self,
        is_enabled: renewalConfig.is_enabled,
      }));
      handleCloseRenewal();
    } catch (err) {
      console.error('Error saving renewal alert:', err);
    }
  };

  const handleDeleteRenewal = async () => {
    try {
      await dispatch(deleteRenewalAlert(popoverContractId));
      handleCloseRenewal();
    } catch (err) {
      console.error('Error deleting renewal alert:', err);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : filtered.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <Typography sx={{ color: theme.palette.text.secondary, fontSize: '0.85rem' }}>
            {t('monitoring.noContracts', 'No contracts with lifecycle data')}
          </Typography>
        </Box>
      ) : (
        <TableContainer sx={{
          borderRadius: '10px',
          border: `1px solid ${theme.palette.divider}`,
          backgroundColor: isDark ? alpha('#fff', 0.02) : '#fff',
        }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{t('monitoring.table.contract', 'Contract')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{t('monitoring.table.party', 'Party')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{t('monitoring.table.expiration', 'Expiration')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{t('monitoring.table.autoRenew', 'Auto-Renew')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{t('monitoring.table.statusBadge', 'Status')}</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{t('monitoring.table.renewalAlert', 'Renewal Alert')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((item, i) => {
                const statusConf = lifecycleStatusConfig[item.lifecycle_status] || lifecycleStatusConfig.active;
                return (
                  <TableRow key={item.id || i} hover>
                    <TableCell>
                      <Typography
                        sx={{
                          fontSize: '0.78rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                          color: theme.palette.primary.main,
                          '&:hover': { textDecoration: 'underline' },
                        }}
                        onClick={() => navigate(`/contract/${item.id}`)}
                      >
                        {item.title || 'Untitled'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.78rem' }}>{item.party || '—'}</TableCell>
                    <TableCell sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary }}>
                      {formatDate(item.expiration_date)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.78rem' }}>
                      {item.auto_renewal ? t('common.yes', 'Yes') : t('common.no', 'No')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={t(statusConf.key, item.lifecycle_status)}
                        size="small"
                        color={statusConf.color}
                        sx={{
                          height: 20, fontSize: '0.65rem', fontWeight: 600,
                          backgroundColor: statusConf.color !== 'default'
                            ? alpha(theme.palette[statusConf.color].main, 0.12)
                            : alpha(theme.palette.text.secondary, 0.12),
                          color: statusConf.color !== 'default'
                            ? theme.palette[statusConf.color].main
                            : theme.palette.text.secondary,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={t('monitoring.configureRenewalAlert', 'Configure renewal alert')}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleOpenRenewal(e, item.id)}
                        >
                          <NotificationsActiveOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Renewal Alert Popover */}
      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={handleCloseRenewal}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            p: 2.5, borderRadius: '12px', minWidth: 300,
            boxShadow: theme.shadows[8],
          },
        }}
      >
        {loadingRenewal ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Stack spacing={2}>
            <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
              {t('monitoring.renewalAlertConfig', 'Renewal Alert')}
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={renewalConfig.is_enabled}
                  onChange={(e) => setRenewalConfig(prev => ({ ...prev, is_enabled: e.target.checked }))}
                  size="small"
                />
              }
              label={t('monitoring.enableAlert', 'Enable email reminder')}
              sx={{ '& .MuiTypography-root': { fontSize: '0.8rem' } }}
            />

            <TextField
              label={t('monitoring.daysBefore', 'Days before expiry')}
              type="number"
              size="small"
              value={renewalConfig.alert_days_before}
              onChange={(e) => setRenewalConfig(prev => ({ ...prev, alert_days_before: parseInt(e.target.value) || 90 }))}
              inputProps={{ min: 1, max: 365 }}
              sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' } }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={renewalConfig.send_to_self}
                  onChange={(e) => setRenewalConfig(prev => ({ ...prev, send_to_self: e.target.checked }))}
                  size="small"
                />
              }
              label={t('monitoring.sendToSelf', 'Send to me')}
              sx={{ '& .MuiTypography-root': { fontSize: '0.8rem' } }}
            />

            <Box>
              <TextField
                label={t('monitoring.alertEmail', 'Add email recipient')}
                size="small"
                placeholder={t('monitoring.defaultUserEmail', 'Enter email and press Enter')}
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const val = emailInput.trim();
                    if (val && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val) && !renewalConfig.alert_emails.includes(val)) {
                      setRenewalConfig(prev => ({ ...prev, alert_emails: [...prev.alert_emails, val] }));
                      setEmailInput('');
                    }
                  }
                }}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => {
                          const val = emailInput.trim();
                          if (val && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val) && !renewalConfig.alert_emails.includes(val)) {
                            setRenewalConfig(prev => ({ ...prev, alert_emails: [...prev.alert_emails, val] }));
                            setEmailInput('');
                          }
                        }}
                        edge="end"
                        disabled={!emailInput.trim()}
                      >
                        <AddCircleOutlineIcon sx={{ fontSize: 18, color: emailInput.trim() ? 'primary.main' : 'text.disabled' }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' } }}
              />
              {renewalConfig.alert_emails.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                  {renewalConfig.alert_emails.map((email) => (
                    <Chip
                      key={email}
                      label={email}
                      size="small"
                      onDelete={() => setRenewalConfig(prev => ({
                        ...prev,
                        alert_emails: prev.alert_emails.filter(e => e !== email),
                      }))}
                      deleteIcon={<CloseIcon sx={{ fontSize: '14px !important' }} />}
                      sx={{ fontSize: '0.7rem', height: 24 }}
                    />
                  ))}
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button
                size="small"
                color="error"
                onClick={handleDeleteRenewal}
                startIcon={<NotificationsOffOutlinedIcon sx={{ fontSize: 14 }} />}
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
              >
                {t('monitoring.removeAlert', 'Remove')}
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleSaveRenewal}
                sx={{
                  textTransform: 'none', fontSize: '0.75rem', fontWeight: 600,
                  bgcolor: isDark ? '#e7e5e4' : '#1c1917',
                  color: isDark ? '#1c1917' : '#fff',
                  '&:hover': { bgcolor: isDark ? '#d6d3d1' : '#292524' },
                }}
              >
                {t('common.save', 'Save')}
              </Button>
            </Box>
          </Stack>
        )}
      </Popover>
    </Box>
  );
};

export default LifecycleList;
